import { useCallback, useEffect, useRef } from 'react';
import pMap from 'p-map';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FigmaCompositorView } from '@/components/Recipe/Views/FigmaCompositorView';
import { FIGMA_BASE_URL } from '@/services/figma';
import { EvaluatedValue, EvaluatedImage } from '@/designer/designer';
import { FigmaCompositorData } from '@/types/node';
import { getOrCreateFigmaApiClient } from '../../Recipe/FlowComponents/Editor/FigmaCompositor/figmaApiClient';
import type { InputId } from 'web';

export const NODE_ID_STANDARD_SEPARATOR = ':';
export const NODE_ID_URL_FORMATTING_SEPARATOR = '-';

const META_START = '<!--(figmeta)';
const META_END = '(/figmeta)-->';
const ESCAPED_META_START = '&lt;!--(figmeta)';
const ESCAPED_META_END = '(/figmeta)--&gt;';

// Convert node_id from API format (colon-delimited) to URL format (hyphen-delimited)
// e.g., "123:456" -> "123-456"
export const getFormattedNodeId = (nodeId: string) => {
  const regex = new RegExp(NODE_ID_STANDARD_SEPARATOR, 'g');
  return nodeId.replace(regex, NODE_ID_URL_FORMATTING_SEPARATOR);
};

export const getFigmaFileUrl = (fileKey: string, nodeId: string) => {
  return FIGMA_BASE_URL + '/file/' + fileKey + '?node-id=' + nodeId;
};

type FigmaPastedMetadata = { fileKey: string; selectedNodeData: string };

export const getFigmaPastedMetadata = (html: string, isFigmaCompositorEnabled: boolean): FigmaPastedMetadata | null => {
  let metaStartIdx = html.indexOf(META_START);
  let metaEndIdx = html.indexOf(META_END);
  let startLen = META_START.length;

  // Handles both unescaped chars from Cmd+C path and escaped chars from right click > "Copy" menu actions
  // (Browsers HTML escape the characters in the navigator API)
  if (metaStartIdx < 0 || metaEndIdx < 0) {
    metaStartIdx = html.indexOf(ESCAPED_META_START);
    metaEndIdx = html.indexOf(ESCAPED_META_END);
    startLen = ESCAPED_META_START.length;
  }

  if (metaStartIdx < 0 || metaEndIdx < 0 || !isFigmaCompositorEnabled) return null;

  try {
    const metaBase64 = html.slice(metaStartIdx + startLen, metaEndIdx);
    const metaJson = decodeURIComponent(escape(atob(metaBase64)));
    return JSON.parse(metaJson) as FigmaPastedMetadata;
  } catch {
    return null;
  }
};

export const PASTED_FIGMA_NODE_OFFSET = 320;

export const getValidPastedFigmaNodes = (
  html: string,
  isFigmaCompositorEnabled: boolean,
): { fileKey: string; nodeId: string }[] => {
  const metadata = getFigmaPastedMetadata(html, isFigmaCompositorEnabled);
  if (!metadata) return [];
  const { fileKey, selectedNodeData } = metadata;
  return selectedNodeData
    .split(',')
    .map((nodeData) => {
      const [nodeId, nodeType, lockedFlag] = nodeData.split('|');
      return { nodeId, nodeType, isLocked: lockedFlag === '1' };
    })
    .filter(({ isLocked }) => !isLocked)
    .map(({ nodeId }) => ({ fileKey, nodeId }));
};

type ImageFileInput = { type: 'image'; value: Extract<EvaluatedImage, { type: 'file' }> };
type TextInput = { type: 'string'; value: string };

// TODO: 'computed' type input support
// https://app.asana.com/1/10497086658021/project/1213205572601284/task/1213455736973407
const isValidImageInput = (inputValue: EvaluatedValue | undefined): inputValue is ImageFileInput =>
  inputValue?.type === 'image' && inputValue.value.type === 'file';

const isValidTextInput = (inputValue: EvaluatedValue | undefined): inputValue is TextInput =>
  inputValue?.type === 'string';

type SyncMutationVars = {
  options: FigmaCompositorData['options'];
  inputs: Record<InputId, EvaluatedValue | undefined>;
  signal: AbortSignal;
};

type ValidInput =
  | { inputId: string; kind: 'image'; value: ImageFileInput }
  | { inputId: string; kind: 'text'; value: TextInput };

const syncImageToFigmaLayer = async (
  fileKey: string,
  figmaRootNodeId: string,
  inputId: string,
  { value }: ImageFileInput,
  view: FigmaCompositorView,
): Promise<void> => {
  const figmaClient = getOrCreateFigmaApiClient();
  const existingNodeId = view.getFigmaNodeId(inputId);
  if (existingNodeId) {
    await figmaClient.updateImageLayer(fileKey, existingNodeId, value.url);
  } else {
    const createdId = await figmaClient.createImageLayer(
      fileKey,
      figmaRootNodeId,
      inputId,
      value.url,
      value.width,
      value.height,
    );
    view.setFigmaNodeId(inputId, createdId);
  }
};

const syncTextToFigmaLayer = async (
  fileKey: string,
  figmaRootNodeId: string,
  inputId: string,
  { value }: TextInput,
  view: FigmaCompositorView,
): Promise<void> => {
  const figmaClient = getOrCreateFigmaApiClient();
  const existingNodeId = view.getFigmaNodeId(inputId);
  if (existingNodeId) {
    await figmaClient.updateTextLayer(fileKey, existingNodeId, value);
  } else {
    const createdId = await figmaClient.createTextLayer(fileKey, figmaRootNodeId, inputId, value);
    view.setFigmaNodeId(inputId, createdId);
  }
};

const syncInputsToFigmaLayers = async (
  { options, inputs, signal }: SyncMutationVars,
  view: FigmaCompositorView,
  setIsRefreshing: (v: boolean) => void,
): Promise<boolean> => {
  const { fileKey, nodeId: figmaNodeId } = options;
  if (!fileKey || !figmaNodeId) return false;

  // Pre-filter to supported types
  const validInputs = Object.entries(inputs).reduce<ValidInput[]>((acc, [inputId, inputValue]) => {
    if (isValidImageInput(inputValue)) acc.push({ inputId, kind: 'image', value: inputValue });
    else if (isValidTextInput(inputValue)) acc.push({ inputId, kind: 'text', value: inputValue });
    return acc;
  }, []);

  // For when a connection is removed
  const syncedInputIds = new Set(validInputs.map((v) => v.inputId));
  for (const inputId of Object.keys(inputs)) {
    if (!syncedInputIds.has(inputId)) {
      view.setFigmaNodeId(inputId, null);
    }
  }

  if (!validInputs.length) return false;

  setIsRefreshing(true);
  try {
    await pMap(
      validInputs,
      ({ inputId, kind, value }) =>
        kind === 'image'
          ? syncImageToFigmaLayer(fileKey, figmaNodeId, inputId, value, view)
          : syncTextToFigmaLayer(fileKey, figmaNodeId, inputId, value, view),
      { signal },
    );
    return true;
  } catch (error) {
    if (signal.aborted) return false;
    throw error; // Throw and surface only when error is from API TODO: show in UI state
  } finally {
    // Only clear the spinner if this run wasn't overtaken by a newer one
    if (!signal.aborted) setIsRefreshing(false);
  }
};

export function useSyncToFigma(
  view: FigmaCompositorView,
  options: FigmaCompositorData['options'],
  setIsRefreshing: (v: boolean) => void,
) {
  const queryClient = useQueryClient();
  const controllerRef = useRef<AbortController>(undefined);

  const { mutate } = useMutation({
    mutationFn: ({ signal }: { signal: AbortSignal }) => {
      const inputs = view.getInputs();
      return syncInputsToFigmaLayers({ options, inputs, signal }, view, setIsRefreshing);
    },
    onSuccess: (anySucceeded) => {
      if (anySucceeded) {
        void queryClient.invalidateQueries({
          queryKey: ['figma-compositor-preview', options.fileKey, options.nodeId],
        });
      }
    },
  });

  // Allows cancellation of any previous in-flight requests
  const triggerSync = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    mutate({ signal: controllerRef.current.signal });
  }, [mutate]);

  useEffect(() => {
    return () => controllerRef.current?.abort();
  }, []);

  return triggerSync;
}
